// Import the RTK Query methods from the React-specific entry point
import { createApi } from '@reduxjs/toolkit/query/react';

import { BIN_15_MINS, BIN_60_MINS } from 'appConstants';
import {
  DatesList,
  LayerRouting,
  MetricsRouteData,
  PlanningData,
  PlanningPeriodsList,
  RouteInfo,
  Segment,
  SegmentInfo,
  SegmentLayer,
  SpeedData,
  TimelineSummary,
  TypicalMonthsList,
} from 'appTypes';
import { RouteQueueData } from 'features/workflow_queuing/common';

import { baseQuery } from './utils';

// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
// https://redux-toolkit.js.org/rtk-query/usage/prefetching

export function urlifyRouteIds(routeIds) {
  if (routeIds) {
    return routeIds.join(',');
  } else {
    return '';
  }
}

// Define our single API slice object
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery,
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    // The `getDates` endpoint is a "query" operation that returns an array of objects:
    // {"dates":  [{"date":"2023-01-21", "layer": 1}, {"date":"2023-01-22", "layer": 1},...] }
    // we convert it to a Map {date: layer}
    getDates: builder.query<DatesList, string>({
      query: (project_slug) => {
        return `${project_slug}/dates/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return rawResult.dates;
      },
    }),
    getTypicalMonths: builder.query<TypicalMonthsList, string>({
      query: (project_slug) => `${project_slug}/typical/`,
      transformResponse: (rawResult: any) => {
        // console.log(`getTypicalMonths transformResponse rawResult: ${JSON.stringify(rawResult)}`);
        const result: TypicalMonthsList = rawResult.months.map((item) => ({
          typicalMonth: item.typical_month,
          layer: item.layer,
          isDetourReady: item.is_detour_ready,
          usesVolumes: item.uses_volumes,
        }));
        // console.log(`getTypicalMonths result: ${JSON.stringify(result)}`);
        return result;
      },
    }),
    getSegments: builder.query<SegmentLayer, number>({
      query: (layerId: number) => {
        const queryString = `/segments/${layerId}/`;
        // console.log(`getSegments queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (
        rawResult: { segments: Array<Segment>; routing?: LayerRouting },
        meta,
        arg,
      ) => {
        // console.log(`getSegments transformResponse ${rawResult.segments.length} segments with layer: ${arg} `);
        return {
          layer: arg,
          data: rawResult.segments,
          routing: rawResult.routing,
        };
      },
    }),
    getSegmentInfo: builder.query<
      SegmentInfo,
      { layerId: number; routeIds: Array<string> }
    >({
      query: ({ layerId, routeIds }) => {
        const queryString = `/segments/${layerId}/${urlifyRouteIds(routeIds)}/`;
        // console.log(`getSegments queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (
        rawResult: { segments: Map<string, Segment> },
        meta,
        arg,
      ) => {
        // console.log(`getSegments transformResponse ${rawResult.segments} segments with layer: ${arg} `);
        return { layer: arg, segments: rawResult.segments };
      },
    }),
    getLayer: builder.query<SegmentLayer, number>({
      query: (layerId: number) => {
        const queryString = `/layer/${layerId}/`;
        // console.log(`getSegments queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (rawResult: { routing?: LayerRouting }, meta, arg) => {
        // console.log(`getSegments transformResponse layer with layer: ${arg} `);
        return { layer: arg, routing: rawResult.routing };
      },
    }),
    getSegmentTranslate: builder.query<
      Array<string>,
      { layerId: number; routeIds: Array<string> }
    >({
      query: ({ layerId, routeIds }) => {
        const queryString = `/segments/translate/${urlifyRouteIds(
          routeIds,
        )}/${layerId}/`;
        // console.log(`getSegments queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (
        rawResult: { segment_ids: Array<string> },
        meta,
        arg,
      ) => {
        // console.log(`getSegmentTranslate transformResponse ${rawResult.segment_ids.length} segments with layer: ${arg.layerId} `);
        return rawResult.segment_ids;
      },
    }),
    getPlanningPeriods: builder.query<PlanningPeriodsList, string>({
      query: (project_slug) => `${project_slug}/planning/`,
      transformResponse: (rawResult: any): PlanningPeriodsList => {
        // console.log(`getPlanningPeriods transformResponse rawResult: ${JSON.stringify(rawResult)}`);
        const result: PlanningPeriodsList = rawResult.months.map((item) => ({
          typicalMonth: item.typical_month,
          layer: item.layer,
          isDetourReady: item.is_detour_ready,
          usesVolumes: item.uses_volumes,
        }));
        // console.log(`getTypicalMonths result: ${JSON.stringify(result)}`);
        return result;
      },
    }),
    getPlanningVolumes: builder.query<
      PlanningData,
      {
        project_slug: string;
        typicalMonth: string;
        segmentId: number;
        binSize: string;
      }
    >({
      //  "planning/(<typicalMonth>/<segmentId>/volume/",
      query: ({ project_slug, typicalMonth, segmentId, binSize }) => {
        console.assert([BIN_15_MINS, BIN_60_MINS].includes(binSize));
        const binParam = binSize === BIN_15_MINS ? '15min' : '1hr';
        const queryString = `${project_slug}/planning/${typicalMonth}/${segmentId}/volume/?period=${binParam}`;
        // console.log(`getPlanningVolumes queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log(`getPlanningVolumes ${JSON.stringify(arg)} rawResult: ${JSON.stringify(rawResult.volumes.slice(0, 4))}`);
        return {
          typicalMonth: arg.typicalMonth,
          segmentId: arg.segmentId,
          binSize: arg.binSize,
          data: rawResult.volumes,
        };
      },
    }),
    getTimelineSummary: builder.query<
      TimelineSummary,
      { project_slug: string; date: string; typicalMonth?: string }
    >({
      query: ({ project_slug, date, typicalMonth }) => {
        let extras = '';
        if (typicalMonth) {
          extras = `/${typicalMonth}`;
        }
        const queryString = `${project_slug}/timeline/summary/${date}${extras}/summary.js`; // .js to enable cloudflare caching
        // console.log(`getSegments queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log(`getSegments transformResponse ${rawResult} segments with layer: ${arg} `);
        return {
          date: arg.date,
          typRelCongestion: rawResult.typ_rel_congestion,
          avgCongestion: rawResult.avg_congestions,
          typRelCounts: rawResult.typ_rel_counts,
          typRelVhd: rawResult.typ_rel_vhd,
        };
      },
    }),
    getRouteMetrics: builder.query<
      MetricsRouteData,
      { project_slug: string; routeIds: Array<string>; date: string }
    >({
      query: ({ project_slug, routeIds, date }) => {
        const queryString = `${project_slug}/segments/${urlifyRouteIds(routeIds)}/metrics/${date}/`;
        // console.log(`getRouteMetrics queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log(`getRouteMetrics ${JSON.stringify(arg)} rawResult: ${JSON.stringify(rawResult.speeds.slice(0, 4))}`);
        return {
          date: arg.date,
          routeIds: arg.routeIds,
          times: rawResult.times,
          bin_size: rawResult.bin_size,
          speeds: rawResult.avg_speed,
          freeflow_traveltime: rawResult.freeflow_traveltime,
        };
      },
    }),
    getRouteMetricsDayRange: builder.query<
      MetricsRouteData,
      {
        project_slug: string;
        routeIds: Array<string>;
        startDate: string;
        endDate: string;
      }
    >({
      query: ({ project_slug, routeIds, startDate, endDate }) =>
        `${project_slug}/segments/${urlifyRouteIds(routeIds)}/metrics_agg/${startDate}/${endDate}/`,
      transformResponse: (rawResult: any, meta, arg) => ({
        date: `${arg.startDate}/${arg.endDate}`,
        routeIds: arg.routeIds,
        times: rawResult.times,
        bin_size: rawResult.bin_size,
        speeds: rawResult.avg_speed,
        freeflow_traveltime: rawResult.freeflow_traveltime,
      }),
    }),
    getTypicalRouteMetrics: builder.query<
      MetricsRouteData,
      {
        project_slug: string;
        routeIds: Array<string>;
        typicalMonth: string;
        date: string;
      }
    >({
      query: ({ project_slug, typicalMonth, routeIds, date }) => {
        const queryString = `${project_slug}/segments/${urlifyRouteIds(routeIds)}/metrics/${typicalMonth}/${date}/`;
        // console.log(`getTypicalRouteMetrics queryString: ${queryString}`);
        return queryString;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log(`getTypicalRouteMetrics ${JSON.stringify(arg)} rawResult: ${JSON.stringify(rawResult.speeds.slice(0, 4))}`);
        return {
          date: arg.date,
          routeIds: arg.routeIds,
          times: rawResult.times,
          speeds: rawResult.avg_speed,
          bin_size: rawResult.bin_size,
        };
      },
    }),
    getRouteInfo: builder.query<
      {
        route: { realizedSegments: string[]; route_length: number };
        feature: RouteInfo;
      },
      {
        project_slug: string;
        segment_ids: Array<string>;
        layer_id: string;
      }
    >({
      query: ({ project_slug, segment_ids, layer_id }) => {
        return `${project_slug}/segments/${layer_id}/route_info/${urlifyRouteIds(
          segment_ids,
        )}/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return {
          route: {
            realizedSegments: rawResult.realized_segments,
            route_length: rawResult.length,
          },
          feature: {
            type: 'Feature',
            geometry: rawResult.geometry,
            properties: {
              route_length: rawResult.route_length,
              cardinal_direction: rawResult.cardinal_direction,
            },
          },
        };
      },
    }),
    // @ts-ignore
    getRouteQueues: builder.query<
      RouteQueueData,
      {
        project_slug: string;
        comparison_ym: string;
        date: string;
        routeIds: Array<string>;
      }
    >({
      query: ({ project_slug, date, comparison_ym, routeIds }) => {
        return `${project_slug}/queues/${comparison_ym}/${date}/${urlifyRouteIds(
          routeIds,
        )}/`;
      },
    }),
    getRouteQueuesDayRange: builder.query<
      RouteQueueData,
      {
        project_slug: string;
        comparison_ym: string;
        startDate: string;
        endDate: string;
        routeIds: Array<string>;
      }
    >({
      query: ({
        project_slug,
        startDate,
        endDate,
        comparison_ym,
        routeIds,
      }) => {
        return `${project_slug}/queues/${comparison_ym}/${startDate}/${endDate}/${urlifyRouteIds(
          routeIds,
        )}/`;
      },
    }),
  }),
});

// Export the auto-generated hook for the `getDates` query endpoint
export const {
  useGetDatesQuery,
  useGetTypicalMonthsQuery,
  useGetSegmentsQuery,
  useGetSegmentInfoQuery,
  useGetLayerQuery,
  useGetSegmentTranslateQuery,
  useGetPlanningPeriodsQuery,
  useGetPlanningVolumesQuery,
  useGetTimelineSummaryQuery,
  useGetTypicalRouteMetricsQuery,
  useGetRouteMetricsQuery,
  useGetRouteMetricsDayRangeQuery,
  useGetRouteInfoQuery,
  useGetRouteQueuesQuery,
  useGetRouteQueuesDayRangeQuery,
} = apiSlice;
